'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { datadogErrorHelper } from '../../common';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';

interface ErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  formatErrorString(error: unknown, info?: React.ErrorInfo): string {
    const e =
      error instanceof Error
        ? error
        : new Error(String(error ?? 'Unknown error'));

    const title = `${e.name || 'Error'}: ${e.message || 'Unknown error'}`;

    const digest = (e as any)?.digest ? `digest: ${(e as any).digest}` : '';

    const code = (e as any)?.code ? `code: ${(e as any).code}` : '';

    const stack = e.stack ?? '';

    const componentStack = info?.componentStack ?? '';

    return [
      title,
      digest,
      code,
      stack && `stack:\n${stack}`,
      componentStack && `reactComponentStack:\n${componentStack}`,
    ]
      .filter(Boolean)
      .join('\n\n');
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // @ts-expect-error: digest is not a standard Error property
    if (error?.digest?.includes('NEXT_REDIRECT')) {
      // ? Skip Next.js redirect errors (NEXT_REDIRECT) caused by calling `redirect` function from next/navigation in server components / functions.

      throw error; // ? pass the error to Next's error boundary
    }

    const errorMessage = this.formatErrorString(error, errorInfo);

    datadogErrorHelper({
      errorMessage,
      errorSeverity: DatadogErrorLevels.ERROR,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
